import * as React from 'react';
import { cn } from '@/lib/utils';

/** True when a keystroke belongs to a text field (or a modal is open on top):
 *  zoning hotkeys must never fire while the user is typing a name, and a
 *  dialog (delete confirm, details form) owns the keyboard. */
export function hotkeyBlocked(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  const tag = el?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return true;
  return !!document.querySelector('[role="dialog"]');
}

/** Single-letter hotkey without modifiers. `keys` lists every layout variant
 *  of the physical key (Latin + Russian), e.g. ['s', 'S', 'ы', 'Ы']. */
export function useZoningHotkey(keys: readonly string[], handler: () => void, enabled = true) {
  const ref = React.useRef(handler);
  ref.current = handler;
  const keySig = keys.join('|');
  React.useEffect(() => {
    if (!enabled) return;
    const list = keySig.split('|');
    const onKey = (e: KeyboardEvent) => {
      if (!list.includes(e.key)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (hotkeyBlocked(e)) return;
      e.preventDefault();
      ref.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keySig, enabled]);
}

/** Hotkey badge shown right on a toolbar button. Hidden on touch screens,
 *  where there is no keyboard to press it on. */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'ml-0.5 hidden rounded border border-current px-1 font-sans text-[10px] leading-4 opacity-60 pointer-fine:inline-block',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
